import type {
    PdfAvailabilityFields,
    PdfAvailabilitySource,
    PdfAvailabilityStatus,
} from './pdfAvailability';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);
const STATUSES = new Set<PdfAvailabilityStatus>([
    'unknown',
    'expected',
    'verified',
    'unavailable',
]);
const SOURCES = new Set<PdfAvailabilitySource>([
    'stored',
    'pmc_cloud',
    'europe_pmc',
    'unpaywall',
]);
const OFFICIAL_REMOTE_SOURCES = new Set<PdfAvailabilitySource>([
    'pmc_cloud',
    'europe_pmc',
    'unpaywall',
]);

export interface PaperPdfAvailabilityResponse extends PdfAvailabilityFields {
    paper_id: string;
    pdf_availability_status: PdfAvailabilityStatus;
    pdf_availability_source: PdfAvailabilitySource | null;
    pdf_url: string | null;
    pdf_availability_license: string | null;
    pdf_availability_checked_at: string | null;
    pdf_availability_next_check_at: string | null;
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function normalizeHttpsPdfCandidate(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const candidate = value.trim();
    if (!candidate) {
        return null;
    }
    try {
        const parsed = new URL(candidate);
        return parsed.protocol === 'https:'
            && Boolean(parsed.hostname)
            && !parsed.username
            && !parsed.password
            ? candidate
            : null;
    } catch {
        return null;
    }
}

export function pdfAvailabilityEndpointCandidates({
    paperId,
    isWeb,
    hostname,
    configuredBaseUrl = process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL,
}: {
    paperId: string;
    isWeb: boolean;
    hostname?: string;
    configuredBaseUrl?: string;
}): string[] {
    const path = `/api/papers/${encodeURIComponent(paperId)}/pdf-availability`;
    if (isWeb) {
        return hostname && LOCAL_HOSTS.has(hostname)
            ? [`${DEFAULT_LOCAL_API_BASE_URL}${path}`]
            : [path];
    }

    return [`${trimTrailingSlash(configuredBaseUrl)}${path}`];
}

export function parsePaperPdfAvailabilityResponse(
    payload: unknown,
    expectedPaperId: string,
): PaperPdfAvailabilityResponse {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('Invalid PDF availability response.');
    }

    const value = payload as Record<string, unknown>;
    const status = value.pdf_availability_status;
    const source = value.pdf_availability_source;
    const pdfUrl = value.pdf_url;
    const normalizedPdfUrl = normalizeHttpsPdfCandidate(pdfUrl);
    const license = value.pdf_availability_license;
    const checkedAt = value.pdf_availability_checked_at;
    const nextCheckAt = value.pdf_availability_next_check_at;
    const validStateEvidence = (
        status === 'verified'
        && source === 'stored'
        && (pdfUrl === null || normalizedPdfUrl !== null)
    ) || (
        status === 'expected'
        && typeof source === 'string'
        && OFFICIAL_REMOTE_SOURCES.has(source as PdfAvailabilitySource)
        && normalizedPdfUrl !== null
    ) || (
        (status === 'unknown' || status === 'unavailable')
        && source === null
        && pdfUrl === null
    );

    if (
        value.paper_id !== expectedPaperId
        || typeof status !== 'string'
        || !STATUSES.has(status as PdfAvailabilityStatus)
        || (source !== null && (typeof source !== 'string' || !SOURCES.has(source as PdfAvailabilitySource)))
        || !validStateEvidence
        || (pdfUrl !== null && normalizedPdfUrl === null)
        || (license !== null && typeof license !== 'string')
        || (checkedAt !== null && typeof checkedAt !== 'string')
        || (nextCheckAt !== null && typeof nextCheckAt !== 'string')
    ) {
        throw new Error('Invalid PDF availability response.');
    }

    return {
        paper_id: expectedPaperId,
        pdf_availability_status: status as PdfAvailabilityStatus,
        pdf_availability_source: source as PdfAvailabilitySource | null,
        pdf_url: normalizedPdfUrl,
        pdf_availability_license: license as string | null,
        pdf_availability_checked_at: checkedAt as string | null,
        pdf_availability_next_check_at: nextCheckAt as string | null,
    };
}
